<script setup lang="ts">
    import { computed, onMounted, watch, shallowReactive, Ref } from 'vue'
    import { useRoute } from 'vue-router'
    import { guid } from '@common/Guid'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import CapexAccountEditor from '@Quest/Capex/Components/CapexAccountEditor.vue'
    import type { CapexAccountEditorFormSubmitEventPayload } from '@Quest/Capex/Components/CapexAccountEditor'
    import { CapexAccountEditorViewModel } from '@Quest/Capex/Components/CapexAccountEditor'
    import type { CapexAccountReadModel, CapexAccountsReadModel } from '@Quest/Capex/readmodels'
    import { useCapexAccountsByStrategyIdQueryV1 } from '@Quest/Capex/queries-vue-composables'

    import {useCreateCapexAccountCommandV1, useUpdateCapexAccountCommandV1, useDeleteCapexAccountCommandV1} from '@Quest/Capex/commands-vue-composables'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    const capexAccountEditor = shallowReactive(new CapexAccountEditorViewModel())

    const {
        runCapexAccountsByStrategyIdQueryV1,
        modelCapexAccountsByStrategyIdQueryV1
    } = useCapexAccountsByStrategyIdQueryV1(workspaceId, strategyId)

    const rm = modelCapexAccountsByStrategyIdQueryV1 as Ref<CapexAccountsReadModel | null>

    const {
        invokeCreateCapexAccountCommandV1,
        succeedCreateCapexAccountCommandV1
    } = useCreateCapexAccountCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateCapexAccountCommandV1,
        succeedUpdateCapexAccountCommandV1
    } = useUpdateCapexAccountCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteCapexAccountCommandV1,
        succeedDeleteCapexAccountCommandV1
    } = useDeleteCapexAccountCommandV1(workspaceId, strategyId)

    watch(
        [
            succeedCreateCapexAccountCommandV1,
            succeedUpdateCapexAccountCommandV1,
            succeedDeleteCapexAccountCommandV1
        ],
        ([create, update, del], [prevCreate, prevUpdate, prevDel]) =>
        {
            if (
                (create && !prevCreate) ||
                (update && !prevUpdate) ||
                (del && !prevDel)
            )
            {
                runCapexAccountsByStrategyIdQueryV1()
                capexAccountEditor.close()
            }
        }
    )

    function showCapexCreator()
    {
        capexAccountEditor.showCreator()
    }

    function showCapexEditor(c: CapexAccountReadModel)
    {
        capexAccountEditor.showEditor(
            c.Id,
            c.Name,
            c.Description,
            c.Classification
        )
    }

    function handleCapexAccountEditorFormSubmit(
        e: CapexAccountEditorFormSubmitEventPayload
    )
    {
        if (e.id == null)
        {
            invokeCreateCapexAccountCommandV1({
                id: guid(),
                name: e.name,
                description: e.description,
                classification: e.classification
            })
        } else
        {
            invokeUpdateCapexAccountCommandV1({
                id: e.id,
                name: e.name,
                description: e.description,
                classification: e.classification
            })
        }
    }

    function handleDeleteCapexAccountCommand(capex: CapexAccountReadModel)
    {
        if (confirm("Are you sure?"))
        {
            invokeDeleteCapexAccountCommandV1({
                id: capex.Id
            })
        }
    }

    onMounted(() =>
    {
        document.title = "Capex | TransparentChoice"
        runCapexAccountsByStrategyIdQueryV1()
    })
</script>

<template>
    <div>
        <CapexAccountEditor :viewModel="capexAccountEditor" 
                            @form-submit="handleCapexAccountEditorFormSubmit" />

        <CorporateNavigation ActivePage="strategy" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Capex</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="showCapexCreator()">Create capex</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div v-if="rm" class="mt-3">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th>Capex</th>
                            <th style="width: 120px;">P&L depreciation</th>
                            <th style="width: 120px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="c in rm.Accounts" :key="c.Id">
                            <td><strong>{{c.Name}}</strong><br />{{c.Description}}</td>
                            <td>{{c.Classification}}</td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" @click="showCapexEditor(c)">Edit</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="handleDeleteCapexAccountCommand(c)">Delete</a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>



